import { useState, Children } from 'react'
import {
  User, Truck, Wrench, Package,
  ClipboardList, Calendar, Hash,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { claimResponseToFormValues } from '@/utils/claimPayloadMapper'
import { useAttributeStore } from '@/store/attributeStore'
import {
  TRUCK_STANDARD_FIELDS, TRAILER_STANDARD_FIELDS,
  TRUCK_ADDITIONAL_FIELDS, TRAILER_ADDITIONAL_FIELDS,
  TRUCK_CONDITIONAL_FIELDS, TRAILER_CONDITIONAL_FIELDS,
} from '@/data/conditionalFields.data'
import { Badge } from '@/components/ui/Badge'
import { getMessage } from '@/utils/messages'
import type { ClaimGetResponse } from '@/types/claim'
import type { LucideIcon } from 'lucide-react'

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtVal(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—'
  if (v === true || v === 'true') return getMessage('claim_view_page.yes')
  if (v === false || v === 'false') return getMessage('claim_view_page.no')
  return String(v)
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return iso
  }
}

/** Heuristic: long/multiline free-text values (notes, comments, textarea-style
 *  attributes) get their own full-width row instead of a cramped grid cell. */
const LONG_TEXT_THRESHOLD = 40
function isLongText(val: string | number | boolean | null | undefined): boolean {
  const s = String(val ?? '')
  return s.includes('\n') || s.length > LONG_TEXT_THRESHOLD
}

function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function statusBadgeVariant(statusId: number): 'neutral' | 'info' | 'success' {
  if (statusId === 1) return 'neutral'
  if (statusId === 2) return 'info'
  return 'success'
}

function statusLabel(statusId: number): string {
  if (statusId === 1) return getMessage('claim_view_page.status.draft')
  if (statusId === 2) return getMessage('claim_view_page.status.submitted')
  return getMessage('claim_view_page.status.completed')
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Single summary tile (File Type / Claim # / Date / Owner) */
function SummaryTile({ icon: Icon, label, value, forPdf }: { icon: LucideIcon; label: string; value: string; forPdf?: boolean }) {
  return (
    <div
      className={cn('bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] px-4 py-3', forPdf ? undefined : 'flex items-center gap-3')}
      style={forPdf ? { display: 'table', width: '100%' } : undefined}
    >
      <div style={forPdf ? { display: 'table-cell', verticalAlign: 'middle', width: 36, paddingRight: 6 } : undefined}>
        <IconBox icon={Icon} forPdf={forPdf} />
      </div>
      <div className="min-w-0" style={forPdf ? { display: 'table-cell', verticalAlign: 'middle' } : undefined}>
        <p className="text-[10px] font-medium text-[var(--text-dim)] uppercase tracking-wide leading-none mb-1">
          {label}
        </p>
        <p className={cn('text-[13px] font-bold text-[var(--text)] leading-snug', forPdf ? 'break-words' : 'truncate')}>
          {value || '—'}
        </p>
      </div>
    </div>
  )
}

/**
 * Small rounded icon square. Centers the icon via fixed padding rather than
 * flexbox — html2canvas only partially supports `align-items`/`justify-content`
 * on flex containers, which showed up as the icon sitting off-center within
 * its box in the rendered PDF even though the outer icon+title row was fixed.
 */
function IconBox({ icon: Icon, forPdf }: { icon: LucideIcon; forPdf?: boolean }) {
  if (forPdf) {
    return (
      <div
        style={{
          width: 36, height: 36, borderRadius: 9, boxSizing: 'border-box',
          padding: 9, border: '1px solid #1e40af66', backgroundColor: '#1e40af1f',
        }}
      >
        <Icon size={16} strokeWidth={2} color="#1e40af" />
      </div>
    )
  }
  return (
    <div
      className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0 border"
      style={{ backgroundColor: '#1e40af1f', borderColor: '#1e40af66', color: '#1e40af' }}
    >
      <Icon size={16} strokeWidth={2} />
    </div>
  )
}

/** Horizontal label: value row inside an InfoCard */
function InfoRow({
  label,
  value,
}: {
  label: string
  value?: string | number | boolean | null
}) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-[5px] border-b border-[var(--border)] last:border-0">
      <span className="text-[13px] font-bold text-[var(--text)] flex-shrink-0">{label}</span>
      <span className="text-[12px] font-normal text-[var(--text-dim)] text-right break-all">{fmtVal(value)}</span>
    </div>
  )
}

/** Stacked label + value cell for dense grid cards */
function GridField({
  label,
  value,
}: {
  label: string
  value?: string | number | boolean | null
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[13px] font-bold text-[var(--text)] leading-tight">
        {label}
      </span>
      <span className="text-[12px] font-normal text-[var(--text-dim)] leading-snug">{fmtVal(value)}</span>
    </div>
  )
}

/**
 * Card header: title only, vertically centered.
 * html2canvas doesn't distribute CSS `line-height` leading symmetrically
 * above/below the glyphs the way browsers do — with a font-size-matched
 * line-height and symmetric container padding, the extra leading still
 * collects mostly above the glyph, pushing it toward the bottom of the
 * banner. Compensating with a smaller top padding than bottom padding
 * (rather than equal padding) brings the visible text back to the
 * vertical center for the PDF render.
 */
function CardHeader({
  icon: Icon,
  title,
  subtitle,
  hasMore,
  expanded,
  onToggle,
  forPdf,
}: {
  icon: LucideIcon
  title: string
  subtitle?: string
  hasMore?: boolean
  expanded?: boolean
  onToggle?: () => void
  forPdf?: boolean
}) {
  if (forPdf) {
    return (
      <div className="pl-[14px] pr-4 pt-[4px] pb-[16px] border-b border-[var(--border)] bg-[#d9edf7] rounded-t-[var(--radius)] text-left">
        <h2
          className="text-[14px] font-bold text-[var(--text)] m-0 text-left"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', lineHeight: '14px' }}
        >
          {title}
        </h2>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-[6px] px-5 py-[15px] border-b border-[var(--border)] bg-[#d9edf7] rounded-t-[var(--radius)]">
      <div
        className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0 border"
        style={{ backgroundColor: '#1e40af1f', borderColor: '#1e40af66', color: '#1e40af' }}
      >
        <Icon size={16} strokeWidth={2} />
      </div>
      <h2
        className="text-[14px] font-bold flex-1 text-[var(--text)]"
        style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
      >
        {title}
      </h2>
      {subtitle && (
        <span className="text-[11px] text-[var(--text-dim)] hidden sm:inline flex-shrink-0">{subtitle}</span>
      )}
      {hasMore && (
        <span
          onClick={onToggle}
          className={cn(
            'text-[var(--text-muted)] text-[20px] flex-shrink-0 cursor-pointer select-none',
            'transition-transform duration-[280ms]',
            expanded && 'rotate-180'
          )}
        >
          ▾
        </span>
      )}
    </div>
  )
}

/** Card with icon header + InfoRows (expandable) — header matches FormPage SectionCard style */
function InfoCard({
  icon: Icon,
  title,
  children,
  expandThreshold,
  className,
  forPdf,
}: {
  icon: LucideIcon
  title: string
  children: React.ReactNode
  expandThreshold?: number
  className?: string
  forPdf?: boolean
}) {
  const [expandedState, setExpanded] = useState(false)
  const expanded = forPdf ? true : expandedState
  const rows = Children.toArray(children)
  const threshold = expandThreshold ?? rows.length
  const visible = expanded ? rows : rows.slice(0, threshold)
  const hasMore = !forPdf && rows.length > threshold

  return (
    <div className={cn('bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden flex flex-col', className)}>
      <CardHeader icon={Icon} title={title} hasMore={hasMore} expanded={expanded} onToggle={() => setExpanded(v => !v)} forPdf={forPdf} />
      <div className="px-5 py-1 flex-1">{visible}</div>
      {hasMore && (
        <div className="px-5 pb-4 pt-1">
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-[12px] text-[var(--accent)] font-mono hover:underline cursor-pointer"
          >
            {expanded
              ? getMessage('claim_view_page.show_less')
              : getMessage('claim_view_page.show_more', { count: String(rows.length - threshold) })}
          </button>
        </div>
      )}
    </div>
  )
}

/** Card with icon header + dense grid of GridFields (expandable) — header matches FormPage SectionCard style */
function GridCard({
  icon: Icon,
  title,
  subtitle,
  children,
  expandThreshold,
  className,
  cols = 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
  forPdf,
}: {
  icon: LucideIcon
  title: string
  subtitle?: string
  children: React.ReactNode
  expandThreshold?: number
  className?: string
  cols?: string
  forPdf?: boolean
}) {
  const [expandedState, setExpanded] = useState(false)
  const expanded = forPdf ? true : expandedState
  const items = Children.toArray(children)
  const threshold = expandThreshold ?? items.length
  const visible = expanded ? items : items.slice(0, threshold)
  const hasMore = !forPdf && items.length > threshold

  return (
    <div className={cn('bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden', className)}>
      <CardHeader icon={Icon} title={title} subtitle={subtitle} hasMore={hasMore} expanded={expanded} onToggle={() => setExpanded(v => !v)} forPdf={forPdf} />
      <div className={cn('grid gap-x-6 gap-y-4 px-5 py-4', cols)}>
        {visible}
      </div>
      {hasMore && (
        <div className="px-5 pb-4">
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-[12px] text-[var(--accent)] font-mono hover:underline cursor-pointer"
          >
            {expanded
              ? getMessage('claim_view_page.show_less')
              : getMessage('claim_view_page.show_more', { count: String(items.length - threshold) })}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── ClaimDetailView ──────────────────────────────────────────────────────────

export function ClaimDetailView({
  claim,
  getLabelById,
  forPdf,
  headerRight,
}: {
  claim: ClaimGetResponse
  getLabelById: (category: string, id: number) => string | undefined
  forPdf?: boolean
  headerRight?: React.ReactNode
}) {
  // ── Derived labels ──────────────────────────────────────────────────────
  const vehicleLabel = getLabelById('vehicle_type', claim.vehicleTypeId) ?? String(claim.vehicleTypeId)
  const stateLabel = claim.state
    ? (getLabelById('country_state', claim.state) ?? String(claim.state))
    : '—'
  const typeOfLossLabel = claim.typeOfLossId
    ? (getLabelById('type_of_loss', claim.typeOfLossId) ?? String(claim.typeOfLossId))
    : '—'

  const isTruck = !!claim.vehicle.truck
  const isTrailer = !!claim.vehicle.trailer
  const stdFields = isTruck ? TRUCK_STANDARD_FIELDS : isTrailer ? TRAILER_STANDARD_FIELDS : []
  const addFields = isTruck ? TRUCK_ADDITIONAL_FIELDS : isTrailer ? TRAILER_ADDITIONAL_FIELDS : []
  const condFields = isTruck ? TRUCK_CONDITIONAL_FIELDS : isTrailer ? TRAILER_CONDITIONAL_FIELDS : []
  const condLabelMap = Object.fromEntries(condFields.map(f => [f.id, f.label]))

  // vin/year/noOfAxles/branded title are already shown in the always-visible
  // "Vehicle Information" card above, so exclude them here to avoid duplicates.
  const DUPLICATE_FIELD_IDS = new Set(['vin', 'year', 'noOfAxles', 'branded', 'conditional_notes'])
  const detailFields = [...stdFields, ...addFields].filter(f => !DUPLICATE_FIELD_IDS.has(f.id))

  const formValues = claimResponseToFormValues(claim, getLabelById, useAttributeStore.getState().cache)
  const standardEquipment = formValues['standard-equipment'] ?? {}
  const additionalOptions = formValues['additional-options'] ?? {}
  const bodyInformation = formValues['body-information'] ?? {}
  const vehicleCondition = formValues['vehicle-condition'] ?? {}

  // Use/Type Subtype (see conditionalFields.data.ts) is stored alongside Use/Type in
  // standard-equipment, not in bodyInformation, even though it's conceptually part of
  // Body Information — shown here explicitly rather than via the bodyInformation loop.
  const bodySubtype = isTruck ? standardEquipment['useSubtype'] : isTrailer ? standardEquipment['typeSubtype'] : ''

  const hasBodyInfo = Object.keys(bodyInformation).length > 0 || !!bodySubtype
  const hasVehicleCondition = Object.keys(vehicleCondition).length > 0

  // Tailwind's sm:/md:/lg: breakpoints key off the browser viewport width, not the
  // width of the (possibly off-screen, fixed-width) container being rasterized for
  // the PDF. Using them there produces a column count that doesn't match the actual
  // render width, causing cramped/overlapping fields. Use fixed column counts instead.
  const summaryCols = forPdf ? 'grid-cols-4' : 'grid-cols-2 md:grid-cols-4'
  const infoCardCols = forPdf ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2'
  const wideGridCols = forPdf ? 'grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
  const narrowGridCols = forPdf ? 'grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'

  return (
    <>
      {/* Page title + status badge — on-screen View page only; the PDF report has
          its own header (logo/title/claim ref) and omits the status badge. */}
      {!forPdf && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex flex-wrap items-center gap-3">
            <h1
              className="text-[18px] md:text-[24px] font-bold text-[var(--text)] leading-tight"
              style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
            >
              {claim.claimReferenceNumber}
            </h1>
            <Badge label={statusLabel(claim.statusId)} variant={statusBadgeVariant(claim.statusId)} />
          </div>
          {headerRight}
        </div>
      )}

      {/* Summary tiles — on-screen View page only; omitted from the PDF report */}
      {!forPdf && (
        <div className={cn('grid gap-3 mb-5', summaryCols)}>
          <SummaryTile forPdf={forPdf} icon={Truck}      label={getMessage('claim_view_page.summary.vehicle_type')}    value={vehicleLabel} />
          <SummaryTile forPdf={forPdf} icon={Hash}       label={getMessage('claim_view_page.summary.claim_number')} value={claim.claimReferenceNumber} />
          <SummaryTile forPdf={forPdf} icon={Calendar}   label={getMessage('claim_view_page.summary.date_received')}value={formatDate(claim.dateClaimReceived)} />
          <SummaryTile forPdf={forPdf} icon={User}       label={getMessage('claim_view_page.summary.owner')}        value={claim.owner ?? '—'} />
        </div>
      )}

      {forPdf ? (
        <>
          {/* Basic Information — full-width grid, matching the other PDF report sections */}
          <GridCard
            icon={ClipboardList}
            title={getMessage('claim_view_page.section.basic_information')}
            cols={narrowGridCols}
            className="mb-4"
            forPdf={forPdf}
          >
            <GridField label={getMessage('claim_view_page.field.claim_ref_number')} value={claim.claimReferenceNumber} />
            <GridField label={getMessage('claim_view_page.field.type_of_loss')} value={typeOfLossLabel} />
            <GridField label={getMessage('claim_view_page.field.date_of_loss')} value={formatDate(claim.dateOfLoss)} />
            <GridField label={getMessage('claim_view_page.field.date_claim_received')} value={formatDate(claim.dateClaimReceived)} />
          </GridCard>

          {/* Owner Information — full-width grid, matching the other PDF report sections */}
          <GridCard
            icon={User}
            title={getMessage('claim_view_page.section.owner_information')}
            cols={narrowGridCols}
            className="mb-4"
            forPdf={forPdf}
          >
            <GridField label={getMessage('claim_view_page.field.owner')} value={claim.owner} />
            <GridField label={getMessage('claim_view_page.field.insured')} value={claim.insured} />
            <GridField label={getMessage('claim_view_page.field.city')} value={claim.city} />
            <GridField label={getMessage('claim_view_page.field.state')} value={stateLabel} />
            <GridField label={getMessage('claim_view_page.field.valuation_zip_code')} value={claim.valuationZipCode} />
          </GridCard>
        </>
      ) : (
        <div className={cn('grid gap-4 mb-4', infoCardCols)}>
          <InfoCard
            icon={ClipboardList}
            title={getMessage('claim_view_page.section.basic_information')}
          >
            <InfoRow label={getMessage('claim_view_page.field.claim_ref_number')} value={claim.claimReferenceNumber} />
            <InfoRow label={getMessage('claim_view_page.field.type_of_loss')} value={typeOfLossLabel} />
            <InfoRow label={getMessage('claim_view_page.field.date_of_loss')} value={formatDate(claim.dateOfLoss)} />
            <InfoRow label={getMessage('claim_view_page.field.date_claim_received')} value={formatDate(claim.dateClaimReceived)} />
          </InfoCard>

          <InfoCard
            icon={User}
            title={getMessage('claim_view_page.section.owner_information')}
          >
            <InfoRow label={getMessage('claim_view_page.field.owner')} value={claim.owner} />
            <InfoRow label={getMessage('claim_view_page.field.insured')} value={claim.insured} />
            <InfoRow label={getMessage('claim_view_page.field.city')} value={claim.city} />
            <InfoRow label={getMessage('claim_view_page.field.state')} value={stateLabel} />
            <InfoRow label={getMessage('claim_view_page.field.valuation_zip_code')} value={claim.valuationZipCode} />
          </InfoCard>
        </div>
      )}

      {/* Vehicle Information — full-width grid (always shown) */}
      <GridCard
        icon={Truck}
        title={getMessage('claim_view_page.section.vehicle_information')}
        expandThreshold={12}
        cols={wideGridCols}
        className="mb-4"
        forPdf={forPdf}
      >
        <GridField label={getMessage('claim_view_page.field.vin')} value={claim.vehicle.vin} />
        <GridField label={getMessage('claim_view_page.field.year')} value={claim.vehicle.year} />
        <GridField label={getMessage('claim_view_page.field.number_of_axles')} value={claim.vehicle.numAxles} />
        <GridField label={getMessage('claim_view_page.field.branded_salvage_title')} value={claim.vehicle.brandedSalvageTitle} />
      </GridCard>

      {/* Truck Details (full-width) */}
      {isTruck && (
        <GridCard
          icon={Truck}
          title={getMessage('claim_view_page.section.truck_details')}
          expandThreshold={12}
          cols={wideGridCols}
          className="mb-4"
          forPdf={forPdf}
        >
          {detailFields.map(f => (
            <GridField key={f.id} label={f.label} value={standardEquipment[f.id] ?? additionalOptions[f.id]} />
          ))}
        </GridCard>
      )}

      {/* Trailer Details (full-width) */}
      {isTrailer && (
        <GridCard
          icon={Package}
          title={getMessage('claim_view_page.section.trailer_details')}
          expandThreshold={12}
          cols={wideGridCols}
          className="mb-4"
          forPdf={forPdf}
        >
          {detailFields.map(f => (
            <GridField key={f.id} label={f.label} value={standardEquipment[f.id] ?? additionalOptions[f.id]} />
          ))}
        </GridCard>
      )}

      {/* Body Information */}
      {hasBodyInfo && (
        <GridCard
          icon={Package}
          title={getMessage('claim_view_page.section.body_information')}
          expandThreshold={8}
          cols={narrowGridCols}
          className="mb-4"
          forPdf={forPdf}
        >
          {!!bodySubtype && (
            <GridField label={getMessage('claim_view_page.field.body_subtype')} value={bodySubtype} />
          )}
          {Object.entries(bodyInformation)
            .filter(([, val]) => !isLongText(val))
            .map(([key, val]) => (
              <GridField
                key={key}
                label={humanizeKey(key)}
                value={val === 'true' ? getMessage('claim_view_page.yes') : val === 'false' ? getMessage('claim_view_page.no') : val}
              />
            ))}
          {Object.entries(bodyInformation)
            .filter(([, val]) => isLongText(val))
            .map(([key, val]) => (
              <div key={key} className="col-span-full break-words">
                <GridField label={humanizeKey(key)} value={val} />
              </div>
            ))}
        </GridCard>
      )}

      {/* Vehicle Condition */}
      {hasVehicleCondition && (
        <GridCard
          icon={Wrench}
          title={getMessage('claim_view_page.section.vehicle_condition')}
          expandThreshold={8}
          cols={narrowGridCols}
          className="mb-4"
          forPdf={forPdf}
        >
          {Object.entries(vehicleCondition)
            .filter(([key]) => key !== 'conditional_notes')
            .map(([key, val]) => (
              <GridField
                key={key}
                label={condLabelMap[key] ?? humanizeKey(key)}
                value={val === 'true' ? getMessage('claim_view_page.yes') : val === 'false' ? getMessage('claim_view_page.no') : val}
              />
            ))}
          {'conditional_notes' in vehicleCondition && (
            <div className="col-span-full break-words">
              <GridField
                label={condLabelMap['conditional_notes'] ?? getMessage('claim_view_page.field.notes')}
                value={vehicleCondition['conditional_notes']}
              />
            </div>
          )}
        </GridCard>
      )}
    </>
  )
}
